import { getApiBaseUrl } from '../config/constants';

interface User {
  email: string;
  name: string;
  role: 'admin' | 'user';
}

interface SignUpResult {
  success: boolean;
  message: string;
  emailVerified?: boolean;
}

interface LoginResult {
  success: boolean;
  token?: string;
  user?: User;
  message: string;
}

interface ConfirmResult {
  success: boolean;
  token?: string;
  user?: User;
  message: string;
}

class CustomAuthService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = getApiBaseUrl();
  }

  async signUp(email: string, password: string, name: string, invitationToken?: string): Promise<SignUpResult> {
    try {
      const body: any = { email, password, name };
      if (invitationToken) {
        body.invitation_token = invitationToken;
      }
      
      const response = await fetch(`${this.apiUrl}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Account creation failed',
        };
      }

      return {
        success: true,
        message: data.message,
        emailVerified: data.email_verified || false,
      };
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async confirmSignUp(email: string, code: string): Promise<ConfirmResult> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Verification failed',
        };
      }

      // Store token and user info
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_info', JSON.stringify(data.user));
      }

      return {
        success: true,
        token: data.token,
        user: data.user,
        message: data.message,
      };
    } catch (error) {
      console.error('Confirmation error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async resendConfirmationCode(email: string): Promise<SignUpResult> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/resend-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Failed to resend confirmation code',
        };
      }

      return {
        success: true,
        message: data.message || 'Confirmation code resent successfully',
      };
    } catch (error) {
      console.error('Resend confirmation error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async signIn(email: string, password: string): Promise<LoginResult> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Login failed',
        };
      }

      // Store token and user info
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_info', JSON.stringify(data.user));
      }

      return {
        success: true,
        token: data.token,
        user: data.user,
        message: data.message,
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Password reset request failed',
        };
      }

      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.detail || 'Password reset failed',
        };
      }

      return {
        success: true,
        message: data.message,
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  async checkAuth(): Promise<User | null> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return null;
      }

      const response = await fetch(`${this.apiUrl}/whoami`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Token is invalid, clear it
        this.logout();
        return null;
      }

      const userData = await response.json();
      const user: User = {
        email: userData.email,
        name: userData.name,
        role: userData.role || 'user',
      };

      // Update stored user info
      localStorage.setItem('user_info', JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Auth check error:', error);
      this.logout();
      return null;
    }
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('id_token'); // Remove old tokens
    localStorage.removeItem('access_token'); // Remove old tokens
    localStorage.removeItem('refresh_token'); // Remove old tokens
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  getUser(): User | null {
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      try {
        return JSON.parse(userInfo);
      } catch (error) {
        console.error('Error parsing user info:', error);
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const user = this.getUser();
    return user?.role === 'admin';
  }
}

// Export singleton instance
export const customAuth = new CustomAuthService();
